
import CommonUtils from "@/common-utils";
import BottomSheets from "@/components/BottomSheets.vue";
import { NotificationSettingVO } from "@/views/biz-kr/mypage/vo/mypage.vo";
import axios from "axios";
import { DateTime } from "luxon";
import { Component, Prop, Vue } from "vue-property-decorator";
import { ProductVO } from "../product/vo/product.vo";
import { RaffleProductVO, RaffleTicketVO, RaffleWinnerHistoryVO } from "./vo/raffle.vo";

@Component({
  components: { BottomSheets },
})
export default class Raffle extends Vue {
  @Prop()
  raffleProductCd!: number;
  raffleProduct = {} as RaffleProductVO;
  product = {} as ProductVO;
  tickets = [] as Array<RaffleTicketVO>;
  winners = [] as Array<RaffleWinnerHistoryVO>;
  raffleNotificationState = {} as NotificationSettingVO;

  remainingTimeInterval;
  remainingTime = "";
  // 1: 하루 이상 남음, 2: 하루 미만 남음, 0: 종료, 3: 진행 예정
  remainingTimeStatus = 99;

  noticePop = false;

  dynamicLinksRaw; // 다이나믹링크

  // 상품 이미지 swiper
  swiperProductImg = {
    centeredSlides: true,
    slidesPerView: 1,
    pagination: { el: ".swiper-pagination-product" },
    autoplay: { delay: 3000, disableOnInteraction: false },
    lazy: true,
    loop: true,
  };

  get ticketCount() {
    return this.tickets.length;
  }

  get brandNm() {
    if (!this.product) {
      return null;
    }
    return this.$i18n.locale == "en" ? this.product.brandEngNm : this.product.brandNm + " " + this.product.brandEngNm;
  }

  // 메인임?
  get isMain() {
    return this.$route.name === "Main";
  }
  // 슬라이드 사용함?
  get isUsingSlides() {
    // 상품 이미지 2개 이상
    return this.product.images && this.product.images.length > 1;
  }

  resetRemainingTime(): void {
    const startTime = DateTime.fromSQL(this.raffleProduct.startDt);
    const endTime = DateTime.fromSQL(this.raffleProduct.endDt);
    const remainingTimeDuration = endTime.diffNow();

    // 진행 예정
    if (startTime.diffNow().as("second") > 0) {
      this.remainingTimeStatus = 3;
      this.remainingTime = `D-${Math.ceil(startTime.diffNow().as("day"))}`;
      return;
    }

    // 하루 이상
    if (remainingTimeDuration.as("hour") > 24) {
      this.remainingTimeStatus = 1;
      this.remainingTime = `D-${Math.ceil(remainingTimeDuration.as("day"))}`;
      return;
    }

    // 하루 미만
    if (remainingTimeDuration.as("second") > 0) {
      this.remainingTimeStatus = 2;
      this.remainingTime = remainingTimeDuration.toFormat("hh:mm:ss");
      return;
    }

    // 종료
    this.remainingTimeStatus = 0;
    this.remainingTime = "00:00:00";
  }

  // 구매 가능?
  get canBuy() {
    // 종료 && 나 당첨자임?
    return this.remainingTimeStatus == 0 && this.raffleProduct.isIamWinner;
  }

  // 공유 링크
  get sharedUrl(): string {
    return `${process.env.VUE_APP_MAIN_URL}raffle/${this.raffleProductCd}?refId=${this.raffleProduct.memId}&refMemCd=${this.raffleProduct.memCd}`;
  }

  // firebase 다이나믹 링크
  async createDynamicLinks(): Promise<string> {
    if (this.dynamicLinksRaw) {
      return this.dynamicLinksRaw;
    }
    return await axios.get("/common/dynamic-links", { params: { link: `${this.sharedUrl}` } }).then((r) => {
      this.dynamicLinksRaw = r.data.shortLink;
      return r.data.shortLink;
    });
  }

  // SNS 공유
  sharedProductUrlToSns(platform: string) {
    if (!this.raffleProduct.memCd) {
      // 로그인이 필요함
      alert(this.$t("common.a0003"));

      this.$router.push({ name: "Login", params: { toName: "Raffles" } });
      return;
    }

    // FIXME  추천정보이기 때문에 다이나믹 링크만 허용
    //        일반 URL로 보내면 추천인정보로 장난질 칠까봐
    this.createDynamicLinks().then((dynamicLinks) => {
      switch (platform) {
        case "kakao": {
          const imageUrl = process.env.VUE_APP_OG_IMAGE;
          const title = process.env.VUE_APP_TITLE || "";

          (this as any).$kakao.Link.sendDefault({
            objectType: "feed",
            content: {
              title: title,
              description: "집으로 오는 면세쇼핑 지쿠",
              imageUrl: imageUrl,
              link: {
                mobileWebUrl: dynamicLinks,
                webUrl: dynamicLinks,
              },
            },
            buttons: [
              {
                title: "앱으로 보기",
                link: {
                  mobileWebUrl: dynamicLinks,
                },
              },
            ],
          });
          break;
        }
        case "facebook":
          break;
        case "instagram":
          // 공유 안되는 것 같음
          break;
        case "copy": {
          const textarea = document.createElement("textarea");
          textarea.value = dynamicLinks;
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand("copy");
          document.body.removeChild(textarea);

          // 복사되었습니다.
          this.$toast(this.$t("product.a0001"));
          break;
        }
        default:
          break;
      }
    });

    BottomSheets.closeAll();
  }

  // 바로결제/장바구니
  goOrder(type: string): void {
    const params = {
      toName: "Product",
      productCd: this.raffleProduct.productCd.toString(),
      count: "1",
      orderCount: "1",
    };

    // 일단 로그인 체크하고
    axios.get("/login/ping").then((r) => {
      if (r.data != "pong") {
        this.$router.push({ name: "Login", params: params });
        return;
      }

      // 로그인 성공 하면
      switch (type) {
        case "cart":
          this.goCart(params);
          break;
        case "payment":
          this.goPayment([params]);
          break;
      }
    });
  }
  // 장바구니
  goCart(params: object): void {
    axios.post("/order/cart", params).then(() => {
      // 잘 담겼습니다. 장바구니로 이동?
      if (confirm(this.$t("product.c0001").toString())) {
        // ㅇㅇ
        this.$router.push({ name: "OrderCart" });
      }
    });
  }

  // 결제
  goPayment(params: Array<object>): void {
    axios
      .post("/order/product/order", params)
      .then((r) => {
        const orderCd = r.data as string;
        this.$router.push({ name: "OrderPayment", params: { orderCd: orderCd } });
      })
      .catch(() => {
        //TODO 실패 사유
      });
  }

  // 응모하기
  btnRaffle() {
    if (process.env.NODE_ENV == "production" && CommonUtils.isApp() == false) {
      // 앱에서만 이용 가능합니다.
        // 모바일 웹
        if (confirm(this.$t("common.c0007").toString())) {
          // APP에서만 이용할 수 있는 서비스입니다.\nAPP으로 이동하시겠습니까?
          if (this.$isMobile) {
            //  this.createDynamicLinks().then((r) => {
            //   location.href = r;
            // });
            location.href = `${process.env.VUE_APP_MAIN_URL}zlink?target=raffle/${this.raffleProductCd}`
          } else {
            //pc에서 재입고알림 누를 시
            location.href = "https://www.zikoo.kr/";
          }
          return;
        }  
      return;
    }

    // 로그인 체크하고
    CommonUtils.checkLoginStatus(this).then((r) => {
      if (!this.tickets.length) {
        // 응모권이 없습니다.
        alert(this.$t("raffle.a0005"));
        return;
      }

      // 노실명인증이면(아이폰)
      if (r.hasRealName == false) {
        // 당첨자 발표를 위해 전화번호 인증이 필요합니다. 이동하시겠습니까?
        if (confirm(this.$t("raffle.c0003").toString()) == false) {
          return;
        }

        // PC면 새창
        if(this.$isMobile == false) {
          window.open(`${CommonUtils.createStaticUrl(this, 'RealNameForm' )}?nextRouteName=MemberInfo`, "_blank", "width=762, height=800")
          return;
        } 

        this.$router.push({ name: "RealNameForm", params: { nextRouteName: "MemberInfo" } });
        return;
      }

      if (!this.tickets.length) {
        // 응모권이 없습니다.
        alert(this.$t("raffle.a0005"));
        return;
      }

      // 응모하시겠습니까?
      if (confirm(this.$t("raffle.c0001").toString()) == false) {
        return;
      }

      /**
       * 응모
       * return 사용자의 래플 알림 설정 정보
       */
      axios.post("/raffle", this.raffleProduct).then((r) => {
        // 래플 알림 미설정
        if( r.data.mailingType == '02' 
            && confirm(this.$t("raffle.c0004").toString()) == true ) {  // 응모완료! 매일 12시 래플 알림을 설정하세요
          // 알림 설정 화면으로 이동
          this.$router.push({name: 'MypageProfile'})
          return;
        }

        this.searchProduct();
      });
    });
  }

  // 응모 취소
  btnCancelRaffle() {
    // 기한이 지난 응모권은 소멸됩니다.\n취소하시겠습니까?
    if (confirm(this.$t("raffle.c0002").toString()) == false) {
      return;
    }
    axios.delete("/raffle", { data: this.raffleProduct }).then(this.searchProduct);
  }

  searchProduct() {
    return axios.get(`/raffle/${this.raffleProductCd}`).then((r) => {
      this.raffleProduct = r.data.raffleProduct;
      this.product = r.data.product;
      this.tickets = r.data.tickets;
      this.winners = r.data.winners || [];
      this.raffleNotificationState = r.data.raffleNotificationState;
    });
  }

  created() {
    this.searchProduct().then(() => {
      // 남은 시간
      this.remainingTimeInterval = setInterval(() => {
        this.resetRemainingTime();
      }, 1000);
    });
  }

  beforeDestory() {
    // 남은 시간
    clearInterval(this.remainingTimeInterval);
  }
}
